import React, { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import JsxParser from 'react-jsx-parser'
import moment from 'moment'
import { KeyboardArrowDown } from '@mui/icons-material'
import { Box, Collapse, GlobalStyles, IconButton, Typography } from '@mui/material'

import { getDateIntervalCellStyles } from '@components/TimeLineGrid'

import { renderJSValueCell } from '@helpers'
import {
  BIND_TYPE,
  TIME_LINE_CELL_MAX_ITEMS_COUNT,
  TIME_LINE_DATE_INTERVAL_CELL_JSX_PARSER_CLASS_NAME,
  TIME_LINE_OBJECT_X_CELL_DEFAULT_PREVIEW_COLUMNS_COUNT,
  TIME_LINE_OBJECT_X_CELL_DEFAULT_PREVIEW_ROWS_COUNT,
  TIME_LINE_OBJECT_X_CELL_MIN_HEIGHT,
  TIME_LINE_OBJECT_X_CELL_MIN_WIDTH,
} from '@constants'
import { ObjectDataRecord } from '@types'

import { PreviewSettingsPopover } from '../PreviewSettingsPopover'

type CellPreviewProps = {
  value?: string
  bindType?: string | null
  settingsPopover?: ReactNode
  previewData: ObjectDataRecord[]
  onApplyToConfig?: (width: number, height: number) => void
}

const generateMockSource = (data: {
  previewData: ObjectDataRecord[]
  cellIndex: number
}): {
  row: {
    id: string
    value: ObjectDataRecord[]
  }
  moment: typeof moment
} => {
  const { previewData, cellIndex } = data

  const remainder = cellIndex % TIME_LINE_CELL_MAX_ITEMS_COUNT
  const shiftsCount = remainder + 1
  const value = Array.from({ length: shiftsCount }, () => previewData[0])

  return {
    row: {
      id: `mock-row-${cellIndex}`,
      value,
    },
    moment,
  }
}

export const CellPreview: React.FC<CellPreviewProps> = ({
  value,
  bindType,
  previewData,
  onApplyToConfig,
}) => {
  const { t } = useTranslation()

  const [previewColumns, setPreviewColumns] = useState(
    TIME_LINE_OBJECT_X_CELL_DEFAULT_PREVIEW_COLUMNS_COUNT
  )
  const [previewRows, setPreviewRows] = useState(TIME_LINE_OBJECT_X_CELL_DEFAULT_PREVIEW_ROWS_COUNT)
  const [previewWidth, setPreviewWidth] = useState(TIME_LINE_OBJECT_X_CELL_MIN_WIDTH)
  const [previewHeight, setPreviewHeight] = useState(TIME_LINE_OBJECT_X_CELL_MIN_HEIGHT)
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(true)

  if (!value || !bindType) {
    return null
  }

  const cells = []

  for (let row = 0; row < previewRows; row++) {
    for (let col = 0; col < previewColumns; col++) {
      const cellIndex = row * previewColumns + col
      const mockSource = generateMockSource({
        cellIndex,
        previewData,
      })

      cells.push({ ...mockSource, key: `cell-${row}-${col}` })
    }
  }

  return (
    <>
      <GlobalStyles styles={getDateIntervalCellStyles()} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ minWidth: 130 }} variant='subtitle2'>
            {t('timeLinesCreateOrEdit.objectX.cell.cellPreview')} ({previewColumns} × {previewRows})
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size='small'
            sx={{
              transform: isPreviewExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
            onClick={() => setIsPreviewExpanded(!isPreviewExpanded)}
          >
            <KeyboardArrowDown />
          </IconButton>
          <PreviewSettingsPopover
            previewColumns={previewColumns}
            previewHeight={previewHeight}
            previewRows={previewRows}
            previewWidth={previewWidth}
            onApplyToConfig={onApplyToConfig}
            onColumnsChange={setPreviewColumns}
            onHeightChange={setPreviewHeight}
            onRowsChange={setPreviewRows}
            onWidthChange={setPreviewWidth}
          />
        </Box>
      </Box>
      <Collapse in={isPreviewExpanded} timeout='auto'>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${previewColumns}, ${previewWidth}px)`,
            overflowX: 'auto',
            p: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: 'background.paper',
          }}
        >
          {cells.map(cell => {
            return (
              <Box
                key={cell.key}
                sx={{
                  width: `${previewWidth}px`,
                  height: `${previewHeight}px`,
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                {cell.row.value?.map((item: ObjectDataRecord, index: number) => {
                  const srcObj = {
                    ...cell,
                    cellItemIndex: index,
                    cellItem: item,
                    cellWidth: previewWidth,
                    cellHeight: previewHeight / cell.row.value?.length,
                  }

                  if (bindType === BIND_TYPE.JS) {
                    return renderJSValueCell(value, srcObj)
                  }

                  return (
                    <JsxParser
                      key={`${cell.key}-${index}`}
                      className={TIME_LINE_DATE_INTERVAL_CELL_JSX_PARSER_CLASS_NAME}
                      jsx={value}
                      bindings={{
                        srcObj,
                        Object,
                        Array,
                        Math,
                      }}
                    />
                  )
                })}
              </Box>
            )
          })}
        </Box>
      </Collapse>
    </>
  )
}
